"use client";

import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Snackbar, Alert } from "@mui/material";

// Import the step components
import PersonalDetails from "../components/profilesetup/PersonalDetails";
import AdditionalDetails from "../components/profilesetup/AdditionalDetails";
import FamilyInformation from "../components/profilesetup/FamilyInformation";
import PartnerPreferences from "../components/profilesetup/PartnerPreferences";
import UploadPhotos from "../components/profilesetup/UploadPhotos";
import UploadHoroscope from "../components/profilesetup/UploadHoroscope";
import UploadIdProof from "../components/profilesetup/UploadIdProof";
import { data, useNavigate } from "react-router-dom";
import apiEndpoints from "../apiConfig"; // make sure path is correct
import { useLocation } from "react-router-dom";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";

export default function MultiStepProfilePage() {
  const [step, setStep] = useState(1);
  const totalSteps = 6;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Robust userId retrieval with logging
  const getUserId = () => {
    const fromState = location.state?.user_id;
    const fromAuth = user?.user_id;
    const fromStorage = localStorage.getItem("user_id");
    const idFromStorage = localStorage.getItem("id");

    const finalId = fromState || fromAuth || fromStorage;
    return finalId;
  };

  const userId = getUserId();
  const Id = user?.id || localStorage.getItem("id");

  useEffect(() => {
    console.log("MultiStepEditModal Debug:", {
      userId,
      Id,
      state: location.state,
      auth: user,
      storage_user_id: localStorage.getItem("user_id"),
      storage_id: localStorage.getItem("id"),
    });
  }, [userId, Id, location.state, user]);

  const [showErrors, setShowErrors] = useState(false);

  // Page names mapping
  const pageNames = {
    1: "Personal Details",
    2: "Family Information",
    3: "Partner Preferences",
    4: "Upload Photos",
    5: "Upload Horoscope",
    6: "Upload ID Proof",
  };

  const { showToast } = useToast();

  const requiredFieldsByStep = {
    // Step 1: Personal Details
    1: [
      "name",
      "email",
      "age",
      "dob",
      "birth_place",
      "birth_time",
      "gender",
      "education",
      "blood_group",
      "address",
      "country",
      "state",
      "city",
      "marital_status",
      "profession",
      "annual_income",
      "religion",
      "caste",
      "zodiac_sign",
      "zodiac_star",
    ],

    // Step 2: Family Information
    2: [
      "family_type",
      "father_name",
      "mother_name",
      "father_occupation",
      "mother_occupation",
      "family_status",
    ],

    // Step 3: Partner Preferences
    3: [
      "pref_religion",
      "pref_country",
      "pref_state",
      "pref_city",
      "pref_marital_status",
      "pref_blood_group",
      "pref_caste",
      "pref_zodiac_sign",
      "pref_zodiac_star",
      "pref_education",
      "pref_profession",
      "pref_annual_income",
    ],

    // Step 4: Upload Photos
    4: ["profile_photo", "profile_photo1", "profile_photo2", "profile_photo3"],
    // Minimum 1 photo required (we'll check length > 0)

    // Step 5: Upload Horoscope
    5: ["horoscope"],

    // Step 6: Upload ID Proof
    6: ["ID_proof"],
  };

  const handleNext = () => {
    const stepFields = requiredFieldsByStep[step] || [];

    const isValid = stepFields.every((field) => {
      // For photo step (step 4), check at least one photo is uploaded
      if (step === 4) {
        return (
          form.profile_photo ||
          form.profile_photo1 ||
          form.profile_photo2 ||
          form.profile_photo3
        );
      }
      // For other steps, check field exists and is not empty
      const value = form[field];
      return value !== null && value !== undefined && value !== "";
    });

    if (!isValid) {
      setShowErrors(true); // you can use this flag to highlight empty fields in your components
      showToast("Please fill all required fields before proceeding", "error");
      return;
    }

    setShowErrors(false);

    // Show toast that current page is updated/completed
    showToast(`${pageNames[step]} updated successfully!`, "success");

    // Move to next step after a short delay to show the toast
    setTimeout(() => {
      setStep((s) => Math.min(totalSteps, s + 1));
    }, 500);
  };

  const handlePrevious = () => {
    setStep((s) => Math.max(1, s - 1));
  };

  const [form, setForm] = useState({
    // Personal
    name: "",
    email: "",
    age: "",
    dob: "",
    birth_place: "",
    birth_time: "",
    gender: "",
    education: "",
    blood_group: "",
    address: "",
    country: "",
    country_id: null,
    state_id: null,
    city_id: null,
    state: "",
    city: "",
    marital_status: "",
    profession: "",
    annual_income: "",
    religion: "",
    caste: "",
    zodiac_sign: "",
    zodiac_star: "",
    about_me: "",

    // Family
    family_type: "",
    father_name: "",
    mother_name: "",
    father_occupation: "",
    mother_occupation: "",
    no_of_brothers: "",
    no_of_sisters: "",
    family_status: "",
    about_family: "",

    // Partner Prefs
    pref_religion: "",
    pref_country: "",
    pref_state: "",
    pref_city: "",
    pref_country_id: null,
    pref_state_id: null,
    pref_city_id: null,
    pref_marital_status: "",
    pref_blood_group: "",
    pref_caste: "",
    pref_zodiac_sign: "",
    pref_zodiac_star: "",
    pref_education: "",
    pref_profession: "",
    pref_annual_income: "",

    profile_photo: null,
    profile_photo1: null,
    profile_photo2: null,
    profile_photo3: null,
    horoscope: null,
    ID_proof: null,
  });

  const progress = (step / totalSteps) * 100;

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleMultiImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const keys = [
      "profile_photo",
      "profile_photo1",
      "profile_photo2",
      "profile_photo3",
    ];

    const readers = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            file, // ✅ keep the actual File object
            name: file.name,
            url: reader.result, // ✅ base64 preview
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((results) => {
      setForm((prev) => {
        const updated = { ...prev };
        let i = 0;
        keys.forEach((key) => {
          if (!updated[key] && i < results.length) {
            updated[key] = results[i]; // ✅ now contains {file, name, url}
            i++;
          }
        });
        return updated;
      });
    });
  };

  const removePhotoAt = (idx) => {
    setForm((p) => ({
      ...p,
      profile_photo: p.profile_photo.filter((_, i) => i !== idx),
    }));
  };

  const handleSingleImageUpload = (e, key) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      alert("Only JPG/PNG files are allowed.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setField(key, {
        file, // ✅ keep the File object
        name: file.name,
        url: ev.target.result, // preview
      });
    };
    reader.readAsDataURL(file);
  };

  const onSubmit = async () => {
    // Fresh check to avoid closure issues
    const currentUserId = userId || localStorage.getItem("user_id");

    if (!currentUserId) {
      showToast("User ID not found. Please sign up first.", "error");
      return;
    }

    // File validations
    if (
      !form.profile_photo &&
      !form.profile_photo1 &&
      !form.profile_photo2 &&
      !form.profile_photo3
    ) {
      showToast("At least 1 photo is required", "error");
      return;
    }
    if (!form.horoscope) {
      showToast("Horoscope image is required", "error");
      return;
    }
    if (!form.ID_proof) {
      showToast("ID Proof is required", "error");
      return;
    }

    try {
      const formData = new FormData();

      // ✅ Use only the allowed fields (same as Admin side)
      const fieldsToSend = [
        "name",
        "email",
        "age",
        "dob",
        "birth_place",
        "birth_time",
        "gender",
        "address",
        "about_me",
        "education",
        "blood_group",
        "country",
        "state",
        "city",
        "marital_status",
        "profession",
        "annual_income",
        "religion",
        "caste",
        "zodiac_sign",
        "zodiac_star",
        "family_type",
        "father_name",
        "mother_name",
        "father_occupation",
        "mother_occupation",
        "no_of_brothers",
        "no_of_sisters",
        "family_status",
        "about_family",
        "pref_religion",
        "pref_country",
        "pref_state",
        "pref_city",
        "pref_marital_status",
        "pref_blood_group",
        "pref_caste",
        "pref_zodiac_sign",
        "pref_zodiac_star",
        "pref_education",
        "pref_profession",
        "pref_annual_income",
        "profile_photo",
        "profile_photo1",
        "profile_photo2",
        "profile_photo3",
        "horoscope",
        "ID_proof",
      ];

      console.log("Preparing to send fields:", form);
      // ✅ Append only those fields
      fieldsToSend.forEach((field) => {
        if (form[field] !== undefined && form[field] !== null) {
          if (form[field]?.file) {
            // ✅ Handle file upload
            formData.append(field, form[field].file);
          } else {
            // ✅ Handle all other types (string, number, boolean/bit)
            formData.append(field, String(form[field]));
          }
        }
      });

      console.log("Submitting form data:", formData);

      // Show loading toast
      showToast("Saving your profile...", "info");

      // Call API
      const res = await fetch(
        `${apiEndpoints.register}?action=update&user_id=${currentUserId}`,
        {
          method: "POST",
          body: formData,
        },
      );
      const data = await res.json();

      if (data.status === "success") {
        showToast("Profile updated successfully", "success");
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        showToast(data.message || "Failed to save profile", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Server error. Could not save profile.", "error");
    }
  };

  // const saveStep = async () => {
  //   if (!userId) return;

  //   try {
  //     const formData = new FormData();

  //     Object.entries(form).forEach(([key, value]) => {
  //       if (!value) return;

  //       if (value && value.url && value.name) {
  //         if (value.url.startsWith("data:")) {
  //           // base64 → Blob
  //           const byteString = atob(value.url.split(",")[1]);
  //           const mimeString = value.url
  //             .split(",")[0]
  //             .split(":")[1]
  //             .split(";")[0];
  //           const ab = new ArrayBuffer(byteString.length);
  //           const ia = new Uint8Array(ab);
  //           for (let i = 0; i < byteString.length; i++) {
  //             ia[i] = byteString.charCodeAt(i);
  //           }
  //           const blob = new Blob([ab], { type: mimeString });
  //           formData.append(
  //             key,
  //             new File([blob], value.name, { type: mimeString })
  //           );
  //         } else {
  //           // already uploaded path
  //           formData.append(key, value.url.replace(apiEndpoints.image, ""));
  //         }
  //       } else if (typeof value === "string" || typeof value === "number") {
  //         formData.append(key, value);
  //       }
  //     });

  //     await fetch(`${apiEndpoints.register}?action=update&id=${userId}`, {
  //       method: "POST",
  //       body: formData,
  //     });
  //   } catch (err) {
  //     console.error("Failed to save step:", err);
  //   }
  // };

  useEffect(() => {
    if (userId) {
      fetch(
        `${apiEndpoints.register}?action=fetch&user_id=${userId}&current_user_id=${Id}`,
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "error") {
            console.error(data.message);
            return;
          }

          const baseUrl = apiEndpoints.image; // <-- use apiEndpoints.image
          const cacheBuster = `?v=${Date.now()}`; // to bust cache

          const toFileObj = (path) =>
            path
              ? {
                  name: path.split("/home").pop(),
                  url: baseUrl + path + cacheBuster, // use apiEndpoints.image
                }
              : null;

          const formatted = {
            ...data,
            // Ensure no null values for text fields
            name: data.name || "",
            email: data.email || "",
            age: data.age || "",
            dob: data.dob || "",
            birth_place: data.birth_place || "",
            birth_time: data.birth_time || "",
            gender: data.gender || "",
            education: data.education || "",
            blood_group: data.blood_group || "",
            address: data.address || "",
            country: data.country || "",
            state: data.state || "",
            city: data.city || "",
            marital_status: data.marital_status || "",
            profession: data.profession || "",
            annual_income: data.annual_income || "",
            religion: data.religion || "",
            caste: data.caste || "",
            zodiac_sign: data.zodiac_sign || "",
            zodiac_star: data.zodiac_star || "",
            about_me: data.about_me || "",

            // Family
            family_type: data.family_type || "",
            father_name: data.father_name || "",
            mother_name: data.mother_name || "",
            father_occupation: data.father_occupation || "",
            mother_occupation: data.mother_occupation || "",
            no_of_brothers: data.no_of_brothers || "",
            no_of_sisters: data.no_of_sisters || "",
            family_status: data.family_status || "",
            about_family: data.about_family || "",

            // Partner Prefs
            pref_religion: data.pref_religion || "",
            pref_country: data.pref_country || "",
            pref_state: data.pref_state || "",
            pref_city: data.pref_city || "",
            pref_marital_status: data.pref_marital_status || "",
            pref_blood_group: data.pref_blood_group || "",
            pref_caste: data.pref_caste || "",
            pref_zodiac_sign: data.pref_zodiac_sign || "",
            pref_zodiac_star: data.pref_zodiac_star || "",
            pref_education: data.pref_education || "",
            pref_profession: data.pref_profession || "",
            pref_annual_income: data.pref_annual_income || "",

            profile_photo: toFileObj(data.profile_photo),
            profile_photo1: toFileObj(data.profile_photo1),
            profile_photo2: toFileObj(data.profile_photo2),
            profile_photo3: toFileObj(data.profile_photo3),
            ID_proof: toFileObj(data.ID_proof),
            horoscope: toFileObj(data.horoscope),
          };

          setForm(formatted);
        })
        .catch((err) => console.error("Fetch error:", err));
    }
  }, [userId]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <PersonalDetails
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );

      case 2:
        return (
          <FamilyInformation
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 3:
        return (
          <PartnerPreferences
            form={form}
            setField={setField}
            isMobile={isMobile}
            showErrors={showErrors}
          />
        );
      case 4:
        return (
          <UploadPhotos
            form={form}
            handleMultiImageUpload={handleMultiImageUpload}
            removePhotoAt={removePhotoAt}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 5:
        return (
          <UploadHoroscope
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      case 6:
        return (
          <UploadIdProof
            form={form}
            handleSingleImageUpload={handleSingleImageUpload}
            setField={setField}
            showErrors={showErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: "90%", mx: "auto", p: { xs: 2, md: 3 } }}>
      <CardHeader
        title={
          <Stack spacing={1}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Profile Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Step {step} of {totalSteps}
            </Typography>
          </Stack>
        }
        // action={
        //   <Box sx={{ minWidth: 240 }}>
        //     <LinearProgress
        //       variant="determinate"
        //       value={progress}
        //       sx={{ mt: 2, height: 8, borderRadius: 2 }}
        //     />
        //   </Box>
        // }
        sx={{ pb: 0 }}
      />

      <CardContent
        sx={{
          overflowY: "auto",
          maxHeight: { xs: "calc(85vh - 160px)", md: "calc(85vh - 170px)" },
          pt: 2,
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {renderStep()}
      </CardContent>

      <CardActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ChevronLeft />}
          disabled={step === 1}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
        >
          Previous
        </Button>

        {step === totalSteps ? (
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            Save Profile
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ChevronRight />}
            onClick={handleNext}
            color="secondary"
          >
            Next
          </Button>
        )}
      </CardActions>
    </Box>
  );
}
